import type { GetMessagesQuery } from '#src/graphql/generated';

import { THREAD_MESSAGE_SUMMARY_MAX_LENGTH } from '#src/constants/message-thread';

export type MessagesQueryRoot = GetMessagesQuery['messages'][number];

export function buildMessageIdToRootIdMap(
  messages: MessagesQueryRoot[],
): Map<string, string> {
  const map = new Map<string, string>();
  for (const root of messages) {
    map.set(root.id, root.id);
    for (const child of root.childMessages ?? []) {
      map.set(child.id, root.id);
    }
  }
  return map;
}

/**
 * Resolves `focusMessageId` query value to a thread root id using loaded root messages
 * (roots include nested `childMessages`).
 */
export function resolveFocusParamToRootId(
  rawFocusId: string | null,
  messages: MessagesQueryRoot[],
): string | null {
  if (!rawFocusId?.trim()) return null;
  return buildMessageIdToRootIdMap(messages).get(rawFocusId.trim()) ?? null;
}

/** Plain-text style summary for collapsed thread lines (markdown/system messages included). */
export function summarizeMessageBody(value: string, maxLen: number): string {
  const collapsed = value.replace(/\s+/g, ' ').trim();
  if (collapsed.length <= maxLen) return collapsed;
  return `${collapsed.slice(0, Math.max(0, maxLen - 1))}…`;
}

export function summarizeMessageForThreadPreview(value: string): string {
  return summarizeMessageBody(value, THREAD_MESSAGE_SUMMARY_MAX_LENGTH);
}
